import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {useParams} from "react-router-dom";
import {Alert, Badge, Button, Card, Col, Container, Row, Spinner} from "react-bootstrap";
import HeroSection from "../../../components/HeroSection.jsx";
import {orderApi} from "../../../api/order.js";

function TaxAuthOrderDetails() {
    const {orderId} = useParams();
    const [orderDetails, setOrderDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const details = await orderApi.getOrder(orderId);
                setOrderDetails(details);
                setError("");
            } catch (e) {
                console.error("Failed to load order details", e);
                setError("Unable to load order details. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [orderId]);

    const openPdf = async () => {
        try {
            const pdf = await orderApi.getOrderPdf(orderDetails.orderId);
            const link = document.createElement("a");
            link.href = `data:application/pdf;base64,${pdf}`;
            link.download = `order-${orderDetails.orderId}.pdf`;
            link.click();
        } catch (e) {
            console.error("Failed to download PDF", e);
            setError("Unable to download PDF for this order.");
        }
    };

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="taxauth"
                eyebrow="Order"
                title={`Order #${orderId}`}
                subtitle="Municipality view"
                actions={orderDetails && (
                    <div className="d-flex gap-2">
                        <Button variant="outline-primary" onClick={openPdf}>Download PDF</Button>
                    </div>
                )}
            />
            {isLoading && (
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading order...</span>
                </div>
            )}
            {error && <Alert variant="danger" className="my-3">{error}</Alert>}
            {!isLoading && !error && orderDetails && (
                <>
                    <Card className="dashboard-panel">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Order Details</strong>
                            <Badge bg="light" text="dark">{orderDetails.status || "Completed"}</Badge>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="mb-2"><strong>Tax Authority:</strong> {orderDetails.taxAuthorityName || "-"}</div>
                                    <div className="mb-2"><strong>Ordered By:</strong> {orderDetails.orderedByUsername || "-"}</div>
                                    <div className="mb-2"><strong>Product:</strong> {orderDetails.productName || "-"}</div>
                                    <div className="mb-2"><strong>Customer Reference:</strong> {orderDetails.customerReference || "-"}</div>
                                    <div className="mb-2"><strong>Search Type:</strong> {orderDetails.searchType?.toUpperCase() || "-"}</div>
                                    <div className="mb-2"><strong>Search Term:</strong> {orderDetails.searchTerm || "-"}</div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-2"><strong>Order Date:</strong> {orderDetails.dateCompleted ? new Date(orderDetails.dateCompleted).toLocaleString() : "-"}</div>
                                    <div className="mb-2"><strong>Total Price:</strong> {orderDetails.totalPrice ? `$${orderDetails.totalPrice.toFixed(2)}` : "-"}</div>
                                    <div className="mb-2"><strong>Total Before Tax:</strong> {orderDetails.totalBeforeTax ? `$${orderDetails.totalBeforeTax.toFixed(2)}` : "-"}</div>
                                    <div className="mb-2"><strong>Product Fee:</strong> {orderDetails.totalProductFee ? `$${orderDetails.totalProductFee.toFixed(2)}` : "-"}</div>
                                    <div className="mb-2"><strong>Service Fee:</strong> {orderDetails.totalServiceFee ? `$${orderDetails.totalServiceFee.toFixed(2)}` : "-"}</div>
                                    <div className="mb-2"><strong>Tax:</strong> {orderDetails.totalTax ? `$${orderDetails.totalTax.toFixed(2)}` : "-"}</div>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={12}>
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <div className="small text-muted">Lookup Type</div>
                                            <div className="fw-bold">{orderDetails.searchType?.toUpperCase() || "-"}</div>
                                        </div>
                                        <div>
                                            <div className="small text-muted">Lookup Term</div>
                                            <div className="fw-bold">{orderDetails.searchTerm}</div>
                                        </div>
                                        <div>
                                            <div className="small text-muted">Order #</div>
                                            <div className="fw-bold">{orderDetails.orderId}</div>
                                        </div>
                                        <div>
                                            <div className="small text-muted">Reference</div>
                                            <div className="fw-bold">{orderDetails.customerReference || "-"}</div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Tax Certificate PDF</strong>
                            <div className="d-flex gap-2">
                                <Button size="sm" variant="outline-primary" onClick={openPdf}>Download</Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {orderDetails.taxCertPdf ? (
                                <iframe
                                    title="Tax Certificate"
                                    src={`data:application/pdf;base64,${orderDetails.taxCertPdf}`}
                                    style={{width: "100%", height: "600px", border: "1px solid #e5e7eb", borderRadius: "8px"}}
                                />
                            ) : (
                                <div className="text-muted">No PDF available for this order.</div>
                            )}
                        </Card.Body>
                    </Card>
                </>
            )}
        </Container>
    );
}

export default observer(TaxAuthOrderDetails);
